import React, { useState, useEffect } from 'react';
import { experimentalStyled, useMediaQuery, Container, Box } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Sidebar from './sidebar/Sidebar';
import Header from './header/Header';
import { TopbarHeight } from '../../assets/global/Theme-variable';
import { updateProfile, updateRole } from '../../redux/profile/action';
import firebase from '../../firebase';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import {
  getCurrentToken,
  getPermissionsByRoleID,
  getUserByID,
  getUserDetailsByUserID,
} from '../../firebase/functions';

const MainWrapper = experimentalStyled('div')(() => ({
  display: 'flex',
  minHeight: '100vh',
  overflow: 'hidden',
  width: '100%',
}));
const PageWrapper = experimentalStyled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden',

  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.up('lg')]: {
    paddingTop: TopbarHeight,
  },
  [theme.breakpoints.down('lg')]: {
    paddingTop: '64px',
  },
}));

const FullLayout = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const customizer = useSelector((state) => state.CustomizerReducer);
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const role = useSelector((state) => state.RoleReducer);

  const getUserDetails = async (e) => {
    const userRecord = await getUserByID(e);
    const data = await getPermissionsByRoleID(userRecord?.role_id);
    const userDetails = await getUserDetailsByUserID(userRecord?.doc_id);
    const token = await getCurrentToken();
    dispatch(updateRole({ info: data }));
    dispatch(
      updateProfile({
        info: {
          user_record: userRecord,
          user_details: userDetails,
        },
        user_role: userRecord?.user_role,
        logged_in: true,
        token: token,
      }),
    );
    localStorage.setItem('token', token);
    axios.interceptors.request.use(
      async function (config) {
        try {
          if (token) {
            config.headers.authorization = `Bearer ${token}`;
          }
          return config;
        } catch (err) {
          return config;
        }
      },
      function (error) {
        return Promise.reject(error);
      },
    );
    axios.interceptors.response.use(
      function (response) {
        return response;
      },
      function (error) {
        if (error?.response?.data) {
          enqueueSnackbar(error.response.data, {
            variant: 'error',
            autoHideDuration: 6000,
          });
        }
        return Promise.reject(error); // Return a rejected promise to propagate the error further
      },
    );
  };

  useEffect(() => {
    firebase?.auth().onAuthStateChanged(async (res) => {
      const eventPathName = window.location.pathname;
      if (!res) {
        if (eventPathName.includes('/events/view-event')) {
          window.location.href = '/partner-signup'
        } else {
          window.location.href = '/auth/login';
        }
      } else {
        await getUserDetails(res?.uid);
      }
    });
  }, []);

  useEffect(() => {
    if (role?.info?.page_permission) {
      const pagePermissions = role?.info?.page_permission?.filter((o) => o.active);
      const access = pagePermissions?.find((o) => o.path === location.pathname)?.access;

      if (location.pathname.includes('/events')) {
        return;
      }

      if (!access || !access.read) {
        const addEventAccess = pagePermissions?.find((o) => o.path === '/add-event')?.access;

        if (!addEventAccess || !addEventAccess.read) {
          const firstAccessiblePage = pagePermissions?.find((o) => o.access?.read)?.path;
          if (firstAccessiblePage) {
            navigate(firstAccessiblePage);
          }
        } else {
          navigate('/add-event');
        }
      }
    }
  }, [location, role, navigate]);

  return (
    <MainWrapper className={customizer.activeMode === 'dark' ? 'darkbg' : ''}>
      <Header
        sx={{
          paddingLeft: isSidebarOpen && lgUp ? '265px' : '',
          backgroundColor: customizer.activeMode === 'dark' ? '#20232a' : '#fafbfb',
        }}
        toggleSidebar={() => setSidebarOpen(!isSidebarOpen)}
        toggleMobileSidebar={() => setMobileSidebarOpen(true)}
      />
      <Sidebar
        isSidebardir={customizer.activeDir === 'ltr' ? 'left' : 'right'}
        isSidebarOpen={isSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        onSidebarClose={() => setMobileSidebarOpen(false)}
      />

      <PageWrapper>
        <Container
          maxWidth={false}
          sx={{
            paddingTop: '20px',
            paddingLeft: isSidebarOpen && lgUp ? '280px!important' : '',
          }}
        >
          <Box sx={{ minHeight: 'calc(100vh - 170px)' }}>
            <Outlet />
          </Box>
        </Container>
      </PageWrapper>
    </MainWrapper>
  );
};

export default FullLayout;
